import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { TrialBalance } from '@/types';


export function TrialBalancePage() {
  const [trialBalance, setTrialBalance] = useState<TrialBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asOf, setAsOf] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchData() {
      try {
        const data = await api.get<TrialBalance>('/api/reports/trial-balance', { errorCode: ErrorCode.FETCH_TRIAL_BALANCE, token: idToken! });
        setTrialBalance(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch trial balance');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchData();
  }, [retryKey, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="flex justify-between items-center pb-4 border-b border-border">
          <div>
            <div className="h-8 w-32 bg-muted rounded mb-2" />
            <div className="h-4 w-48 bg-muted rounded" />
          </div>
          <div className="flex gap-3">
            <div className="h-9 w-36 bg-muted rounded" />
            <div className="h-8 w-24 bg-muted rounded-md" />
            <div className="h-8 w-16 bg-muted rounded-md" />
          </div>
        </div>
        <div className="bg-card rounded-lg border border-border p-6">
          <div className="flex items-center gap-3">
            <div className="h-6 w-6 bg-muted rounded-full" />
            <div className="space-y-2">
              <div className="h-5 w-36 bg-muted rounded" />
              <div className="h-4 w-48 bg-muted rounded" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 space-y-2">
              <div className="h-4 w-20 bg-muted rounded" />
              <div className="h-7 w-24 bg-muted rounded" />
            </div>
          ))}
        </div>
        {[1, 2].map(i => (
          <div key={i} className="bg-card rounded-lg border border-border">
            <div className="p-6 pb-2 flex justify-between">
              <div className="h-5 w-24 bg-muted rounded" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
            <div className="p-6 pt-0 space-y-2">
              {[1, 2, 3].map(j => (
                <div key={j} className="flex justify-between">
                  <div className="h-4 w-32 bg-muted rounded" />
                  <div className="h-4 w-16 bg-muted rounded" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error || !trialBalance) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error ?? 'Unable to load trial balance'}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  // Group accounts by type
  const groupedAccounts = (trialBalance.accounts ?? []).reduce((acc, account) => {
    acc[account.type] ??= [];
    acc[account.type].push(account);
    return acc;
  }, {} as Record<string, NonNullable<typeof trialBalance.accounts>>);

  const typeOrder = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
  const typeLabels: Record<string, string> = {
    ASSET: 'Assets',
    LIABILITY: 'Liabilities',
    EQUITY: 'Equity',
    REVENUE: 'Revenue',
    EXPENSE: 'Expenses',
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Trial Balance</h1>
          <p className="text-sm text-muted-foreground">Verify that total debits equal total credits</p>
        </div>
        <div className="flex gap-3">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            placeholder="As of date"
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print</Button>
        </div>
      </div>

      {/* Balance Status */}
      <Card className={trialBalance.is_balanced ? 'border-primary/30 bg-primary/5' : 'border-destructive/30 bg-destructive/5'}>
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            {trialBalance.is_balanced ? (
              <>
                <i className="bi bi-check-circle text-xl text-success flex-shrink-0"></i>
                <div>
                  <h3 className="font-semibold text-success">Books are balanced</h3>
                  <p className="text-sm text-muted-foreground">Total debits equal total credits</p>
                </div>
              </>
            ) : (
              <>
                <i className="bi bi-exclamation-triangle-fill text-xl text-danger flex-shrink-0"></i>
                <div>
                  <h3 className="font-semibold text-danger">Books are out of balance</h3>
                  <p className="text-sm text-danger">Difference: {formatCurrency(Number(trialBalance.difference))}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Debits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tabular-nums">
              {formatCurrency(Number(trialBalance.total_debits))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Credits</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tabular-nums">
              {formatCurrency(Number(trialBalance.total_credits))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Difference</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono tabular-nums ${
              trialBalance.is_balanced ? 'text-success' : 'text-danger'
            }`}>
              {formatCurrency(Math.abs(Number(trialBalance.difference)))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trial Balance by Type */}
      {typeOrder.map((type) => {
        const accounts = groupedAccounts[type] ?? [];
        if (accounts.length === 0) return null;

        const typeDebits = accounts.reduce((sum, a) => sum + Number(a.debit), 0);
        const typeCredits = accounts.reduce((sum, a) => sum + Number(a.credit), 0);

        return (
          <Card key={type}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle>{typeLabels[type]}</CardTitle>
                <div className="text-sm text-muted-foreground">
                  <span className="mr-4">Dr: {formatCurrency(typeDebits)}</span>
                  <span>Cr: {formatCurrency(typeCredits)}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Code</th>
                      <th className="py-2 px-3 text-left font-medium text-muted-foreground">Account</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Debit</th>
                      <th className="py-2 px-3 text-right font-medium text-muted-foreground">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account) => (
                      <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                        <td className="py-2 px-3"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{account.code}</code></td>
                        <td className="py-2 px-3">{account.name}</td>
                        <td className="py-2 px-3 text-right font-mono">
                          {Number(account.debit) > 0 ? formatCurrency(Number(account.debit)) : ''}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {Number(account.credit) > 0 ? formatCurrency(Number(account.credit)) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
